const addLeading1ToPhoneNumberForApi = (phone) => `1${phone}`;

const maxCents = (extension, pricing) => {
  let maxCents = pricing.cents;
  for (let tier of extension.tiers) {
    if (tier.cents > maxCents) {
      maxCents = tier.cents;
    }
  }
  return maxCents;
};

export const buildTermString = (
  byweight,
  { maxoffers, pricing, type, extension },
  priceIncreaseEnabled,
  isSingleTerm,
) => {
  const formatByWeightPromotionOffer = () =>
    byweight ? (parseFloat(pricing.qty) > 1 ? ` lbs` : ` lb`) : "";

  const formatPromotionOfferLimit = () =>
    maxoffers ? `(Max ${maxoffers})` : null;

  const formatQuantity = (qty) => (qty === 1 ? `each` : `/ ${qty}`);

  const formatCentsOff = () => {
    if (extension?.tiers?.length && isSingleTerm) {
      return {
        offer: `Save up to ${formatCurrency(maxCents(extension, pricing))}`,
        limit: formatPromotionOfferLimit(),
      };
    }
    return {
      offer: `Buy ${
        pricing.qty
      }${formatByWeightPromotionOffer()}, Save ${formatCurrency(
        pricing.cents,
      )}${extension?.tiers?.length ? "*" : ""}`,
      limit: formatPromotionOfferLimit(),
    };
  };

  const formatMustBuyQuantityNewPrice = () => {
    return {
      offer: `Buy ${
        pricing.qty
      }${formatByWeightPromotionOffer()} for ${formatCurrency(pricing.cents)}`,
      limit: formatPromotionOfferLimit(),
    };
  };

  const formatNewPrice = () => {
    return priceIncreaseEnabled
      ? {
          offer: `${formatCurrency(pricing.cents)} off ${formatQuantity(
            pricing.qty,
          )}`,
          limit: formatPromotionOfferLimit(),
        }
      : formatMustBuyQuantityNewPrice();
  };

  const formatMultiPack = () => {
    return {
      offer: `Buy ${
        pricing.qty
      }+${formatByWeightPromotionOffer()}, Save ${formatCurrency(
        pricing.cents,
      )} ea.`,
      limit: formatPromotionOfferLimit(),
    };
  };

  const formatPercentOff = () => {
    return {
      offer: `Buy ${pricing.qty}${formatByWeightPromotionOffer()}, ${
        pricing.cents
      }% off`,
      limit: formatPromotionOfferLimit(),
    };
  };

  const termsMap = new Map([
    ["discount", formatCentsOff],
    ["mbq-discount", formatCentsOff],
    ["mbq-percentdiscount", formatPercentOff],
    ["mbq-price", formatMustBuyQuantityNewPrice],
    ["mpo-discount", formatCentsOff],
    ["mpr-discount", formatMultiPack],
    ["new price", formatNewPrice],
    ["percentdiscount", formatPercentOff],
    ["price", formatNewPrice],
  ]);

  const formatter = termsMap.get(type);
  if (!formatter) {
    console.warn(`Warning: No formatting function found for promo type: '${type}'`);
    return { offer: '', limit: null };
  }

  return formatter();
};

export const byWeightDisplay = (qty) => {
  return parseFloat(qty) > 1 ? `${qty} lbs` : `${qty} lb`;
};

const check = {
  any: (minRequired, maxAllowed) => !minRequired && !maxAllowed,
  minOrMore: (minRequired, maxAllowed) => minRequired > 0 && !maxAllowed,
  exact: (minRequired, maxAllowed) => minRequired && minRequired === maxAllowed,
  atLeastUpTo: (minRequired, maxAllowed) => minRequired > 0 && maxAllowed > 0,
  one: (maxAllowed) => maxAllowed === 1,
  upTo: (minRequired, maxAllowed) => !minRequired && maxAllowed,
};

export const checkModifierGroupValidity = (
  modifierGroup,
  numberCheckedInGroup,
) => {
  const { min_required: minRequired, max_allowed: maxAllowed } = modifierGroup;

  if (check.any(minRequired, maxAllowed)) {
    return true;
  }
  if (check.minOrMore(minRequired, maxAllowed)) {
    return numberCheckedInGroup >= minRequired;
  }
  if (check.exact(minRequired, maxAllowed)) {
    return numberCheckedInGroup === minRequired;
  }
  if (check.atLeastUpTo(minRequired, maxAllowed)) {
    if (check.one(maxAllowed)) {
      return numberCheckedInGroup === 1;
    }
    return (
      numberCheckedInGroup >= minRequired && numberCheckedInGroup <= maxAllowed
    );
  }
  if (check.upTo(minRequired, maxAllowed)) {
    return numberCheckedInGroup <= maxAllowed;
  }
};

export const formatAddress = (singleline) => {
  return singleline ? singleline.replace(", USA", "") : null;
};

export const formatCurrency = (cents) => {
  // integer. may be 0.
  if (!cents && cents !== 0) {
    return null;
  }
  const dollars = cents / 100;

  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

export const formatPhoneNumber = (val) => {
  if (!val) {
    // phone number was not required in early version of storefront
    return;
  }
  const x = val
    .replace(/\D/g, "")
    .slice(-10)
    .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  return !x[2] ? x[1] : "(" + x[1] + ") " + x[2] + (x[3] ? "-" + x[3] : "");
};

export const formatPhoneNumberForApi = (phone) => {
  if (!phone) {
    return;
  }
  return addLeading1ToPhoneNumberForApi(removeAllNonNumericCharacters(phone));
};

export const formatToFileName = (departmentName) => {
  return departmentName.replace(/[ ,.]/g, "").toLowerCase();
};

export const getModifierGroupHeaderText = (modifierGroup) => {
  const { min_required: minRequired, name } = modifierGroup;
  if (!minRequired) {
    return `${name} (Optional)`;
  }
  return `${name} (Required)`;
};

export const getModifierGroupRequirementsText = (modifierGroup) => {
  const { min_required: minRequired, max_allowed: maxAllowed } = modifierGroup;

  if (check.any(minRequired, maxAllowed)) {
    return "Pick Any";
  }
  if (check.minOrMore(minRequired, maxAllowed)) {
    return `Pick ${minRequired} or More`;
  }
  if (check.exact(minRequired, maxAllowed)) {
    return `Pick ${minRequired}`;
  }
  if (check.atLeastUpTo(minRequired, maxAllowed)) {
    if (check.one(maxAllowed)) {
      return "Pick 1";
    }
    return `Pick at Least ${minRequired}, Max ${maxAllowed}`;
  }
  if (check.upTo(minRequired, maxAllowed)) {
    return `Pick up to ${maxAllowed}`;
  }
};

export const getEstimatedPrice = (product) => {
  const estimatedPriceOfModifiers = getEstimatedPriceOfModifiers(product);
  // const estimatedUnitPriceOfProduct = getEstimatedUnitPriceOfProduct(product);
  return (product.cents + estimatedPriceOfModifiers) * product.qty;
};

const getEstimatedPriceOfModifiers = (product) => {
  return product.modifiers
    ? product.modifiers.reduce((a, b) => {
        return a + b.cents;
      }, 0)
    : 0;
};

const getNodeEnv = () => {
  return process.env.NODE_ENV !== "production" ? process.env.NODE_ENV : "";
};

export const getProductDepartmentName = (parentCategories, categories) => {
  return parentCategories.length &&
    parentCategories[0].category_name &&
    parentCategories[0].category_name !== "no-parent"
    ? parentCategories[0].category_name
    : categories[0].category_name;
};

export const getProductDetailViewFromProductState = (product) => {
  /*
   * possible product states:
   * 1. non-modifier (default)
   * 2. modifier
   * 3. modifier in cart
   */
  const stateToViewPathMap = {
    default: "details",
    modifier: "modifiers",
    modifierInCart: "addFromCart",
  };
  return !product.modifier_groups
    ? stateToViewPathMap.default
    : product.item_id || product.cartItems
    ? stateToViewPathMap.modifierInCart // in cart
    : stateToViewPathMap.modifier; // not in cart
};

export const getItemsAndQuantityInCart = (products, product) => {
  // can be multiple products with same upc due to different modifiers
  if (!products || !product.upc) {
    return;
  }
  const productsInCartWithSameUpc = products.filter((p) => {
    return (
      product.upc === p.upc &&
      (product.is_redemption ?? false) === (p.is_redemption ?? false) &&
      product.redemption_points_offer_id == p.redemption_points_offer_id
    );
  });
  return productsInCartWithSameUpc.length
    ? productsInCartWithSameUpc.length >= 1 &&
      productsInCartWithSameUpc[0].modifier_groups
      ? reviseProductItemsAndQuantityToIncludeModifiedVersions(
          productsInCartWithSameUpc,
        )
      : // modifier with 1 or more in cart
        {
          item_id: productsInCartWithSameUpc[0].item_id,
          cartQty: productsInCartWithSameUpc[0].qty,
          product_session: productsInCartWithSameUpc[0].product_session,
        }
    : // non-modifier (can only have 1)
      {
        item_id: null,
        cartQty: 0,
      };
};

export const getStoreName = () => {
  const pathname = window.location.pathname.replace(/^\/([^/]*).*$/, "$1");
  return pathname ? pathname : "invalid-store-name";
};

export const getStoreURL = () => {
  const url = new URL(window.location.href);
  const origin = url.origin;
  const pathName = url.pathname;
  const urlName = pathName.split("/")[1];
  return `${origin}/${urlName}`;
};

export const getUnitPriceWithModifiers = (product) => {
  const priceOfModifiers = getEstimatedPriceOfModifiers(product);
  return product.unitPrice + priceOfModifiers;
};

export const nearestFutureMinutes = (interval, earliestStartMoment) => {
  const roundedMinutes =
    Math.ceil(earliestStartMoment.minute() / interval) * interval;
  return earliestStartMoment.clone().minute(roundedMinutes).second(0);
};

export const packageProductsForOrderDetails = (items) => {
  return Object.values(items)
    .filter(removeDeliveryFeeInProductList)
    .map(packageProduct);
};

const packageProduct = (item) => {
  return {
    byweight: item.byweight,
    cents: item.priceWithModifiers / item.qty,
    is_redemption: item.is_redemption,
    modifiers: item.modifiers,
    name: item.name,
    priceWithModifiers: item.priceWithModifiers,
    qty: item.byweight ? item.qty / 100 : item.qty,
    redemption_points_offer_id: item.redemption_points_offer_id,
    redemption_points_offer: item.redemption_points_offer,
    item_id: item.item_id,
    upc: item.upc,
  };
};

export const removeAllNonNumericCharacters = (phone) => {
  return ("" + phone).replace(/\D/g, "");
};

export const removeCommasAndSpaces = (str) => {
  return str.replace(/[, ]/g, "");
};

const removeDeliveryFeeInProductList = (item) => {
  return !item.name.includes("Delivery Fee");
};

export const removeLeading1OnPhoneNumberForClientUse = (phoneNumber) => {
  const leading1 = phoneNumber?.charAt(0) === "1";
  return leading1 ? phoneNumber.substring(1) : phoneNumber;
};

export const removeLongWordsFromProductName = (productName) => {
  const charactersInALongWord = 35;
  const defaultProductName = "Product";

  return productName
    ? productName
        .split(" ")
        .filter((word) => word.length < charactersInALongWord)
        .join(" ")
    : defaultProductName;
};

const removeUnusedPromotionData = (products, promotions) => {
  // 1. include only the promotions that have a basket item upc in the upcs array
  // 2. remove any upc from the promotion upcs array that is not an item in the basket
  const matchUPCs = (upc) => products.some((product) => product.upc === upc);
  return promotions?.reduce((promos, promotion) => {
    const matchingUPCs = promotion.upcs.filter(matchUPCs);
    if (matchingUPCs.length) {
      promos.push({
        ...promotion,
        upcs: matchingUPCs,
      });
    }

    return promos;
  }, []);
};

const removeUnusedModifierGroups = (products, modifierGroups) => {
  let modifierGroupsInCart = products.flatMap((product) =>
    product.modifiers?.map((modifier) => modifier.modifier_group),
  );
  let filteredGroups = modifierGroups.filter((obj) =>
    modifierGroupsInCart.includes(obj.modifier_group),
  );
  return filteredGroups;
};

export const requireDepartmentPNGIcon = (departmentName) => {
  try {
    return require(
      `@/assets/departments/corner/dep_${formatToFileName(departmentName)}.png`,
    );
  } catch {
    // no fallback image. blank is better
    return;
  }
};

export const requireDepartmentPNGIconSrcset = (departmentName) => {
  let srcsetArr = [];
  [1, 2, 3, 4].forEach((dpr) => {
    srcsetArr.push(requireDepartmentPNGIconDpr(departmentName, dpr));
  });
  return srcsetArr.filter(Boolean).join(", ");
};

const requireDepartmentPNGIconDpr = (departmentName, dpr) => {
  try {
    return (
      require(
        `@/assets/departments/corner/dep_${formatToFileName(
          departmentName,
        )}@${dpr}x.png`,
      ) + ` ${dpr}x`
    );
  } catch {
    // no fallback image. blank is better
    return;
  }
};
const reviseProductItemsAndQuantityToIncludeModifiedVersions = (
  productsInCartWithSameUpc,
) => {
  const cartQty = productsInCartWithSameUpc.reduce(
    (a, b) => a + parseFloat(b.qty),
    0,
  );
  return { cartItems: productsInCartWithSameUpc, cartQty };
};
export const setDocumentTitle = (storeName) => {
  document.title = `Shop ${storeName} ${getNodeEnv()}`;
};

export const truncate = (s, breakpoint) => {
  const chars = {
    xs: 27,
    sm: 22,
    md: 28,
    lg: 28,
    xl: 28,
  };

  return s.length > chars[breakpoint]
    ? `${s.substring(0, chars[breakpoint])}...`
    : s;
};

export const twentyFourHourToAmPm = (twentyFourHourTime) => {
  const hours = parseInt(twentyFourHourTime.slice(0, 2));
  const m = twentyFourHourTime.slice(3);
  const suffix = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${h}:${m} ${suffix}`;
};

export const updateObjectInArray = (array, object, id) => {
  return array.map((item) => {
    if (item[id] !== object[id]) {
      // this isn't the item we care about - keep it as-is
      return item;
    }
    // otherwise, this is the one we want - return an updated value
    return {
      ...item,
      ...object,
    };
  });
};

export const useSharedLibCart = (
  products,
  promotions,
  modifierGroups,
  deliveryAmount,
  firstOrderDiscount,
  feeBusterConfig,
  driverTip,
  storeTip,
  points_config,
  price_increase,
) => {
  return {
    order_id: null,
    products,
    promotions: removeUnusedPromotionData(products, promotions),
    brnumber: true,
    /*
     * passing in true instead of account number.
     * we are not (yet) honoring br club promo
     * limits which check the br club number for
     * how many times user has used the br club
     * promotions discount.
     */
    extpromo_cloud_usage: null,
    modifierGroups: removeUnusedModifierGroups(products, modifierGroups),
    delivery_amount: deliveryAmount,
    first_order_discount: firstOrderDiscount,
    fee_buster_config: feeBusterConfig,
    driver_tip: driverTip,
    store_tip: storeTip,
    points_config,
    price_increase,
    basket_data: false,
  };
};

export const wait = (object, prop) => {
  return object ? object[prop] : null;
};
